import type { FloraCapabilitiesResponse } from '../../../../api/agents-client/agent-client.type';

/**
 * The sections of the Flora settings area, used as the `:section` route segment.
 */
enum FloraSettingsSection {
  Profile = 'profile',
  Memory = 'memory',
  General = 'general',
  Tools = 'tools',
  Connections = 'connections',
}

/**
 * The connections the frontend knows how to manage, used as the `:appId`
 * route segment. Google and Office match the capability catalog's bucket ids;
 * the catalog may add buckets the frontend renders generically.
 */
enum FloraConnectionId {
  Google = 'google',
  Office = 'office',
  Slack = 'slack',
  Teams = 'teams',
}

/**
 * One entry in the settings left menu.
 */
interface FloraSettingsNavItem {
  section: FloraSettingsSection;
  label: string;
  icon: string;
  /** Background of the section's icon tile (menu and page heading). */
  tileBackground: string;
  /** Glyph colour of the section's icon tile. */
  tileColor: string;
}

/**
 * A labelled group of entries in the settings left menu.
 */
interface FloraSettingsNavGroup {
  label: string;
  items: FloraSettingsNavItem[];
}

/**
 * Connection status read from one integration's status endpoint.
 */
interface IntegrationStatusInput {
  /** False when the integration's feature flag is off; the row is left out. */
  enabled: boolean;
  /** The workspace/tenant app is installed (Slack/Teams). */
  installed: boolean;
  /** The current user's own account is linked. */
  userConnected: boolean;
}

interface BuildConnectionListInput {
  capabilities: FloraCapabilitiesResponse | undefined;
  google: { connected: boolean; email: string | null } | undefined;
  slack: IntegrationStatusInput;
  teams: IntegrationStatusInput;
}

/**
 * One row of the Connections list.
 */
interface FloraConnectionListItem {
  id: string;
  name: string;
  /** What the connection lets Flora do, or its connection detail when connected. */
  description: string;
  connected: boolean;
  /** Tools Flora gets from the connection's catalog bucket, when it has one. */
  toolCount: number | null;
}

export { FloraConnectionId, FloraSettingsSection };
export type {
  BuildConnectionListInput,
  FloraConnectionListItem,
  FloraSettingsNavGroup,
  FloraSettingsNavItem,
  IntegrationStatusInput,
};
